/**
 * Utility functions for communicating with the opener window
 */

// Get a safe target origin for postMessage
export function getOpenerOrigin(): string | null {
  if (typeof window === "undefined" || !window.opener) {
    return null
  }

  // Try to get the opener's origin directly (may throw if cross-origin)
  try {
    return window.opener.location.origin
  } catch {
    // Cross-origin - use document.referrer as fallback
    if (document.referrer) {
      try {
        return new URL(document.referrer).origin
      } catch {
        // Invalid referrer URL
      }
    }
  }

  return null
}

// Send a message to the opener window (popup opened via window.open())
export function sendMessageToParent(message: any) {
  if (typeof window === "undefined") {
    console.warn("Cannot send message: window not available")
    return
  }

  if (!window.opener) {
    console.warn("Cannot send message: no opener window available")
    return
  }

  if (window.opener.closed) {
    console.warn("Cannot send message: opener window is closed")
    return
  }

  const targetOrigin = getOpenerOrigin()
  if (!targetOrigin) {
    console.warn("Cannot send message: unable to determine trusted origin")
    return
  }

  window.opener.postMessage(message, targetOrigin)
}

// Request a token from the parent window
export function requestTokenFromParent() {
  sendMessageToParent({ action: "REQUEST_TOKEN" })
}

// Request a new token due to authentication failure
export function requestTokenDueToAuthFailure(error?: string) {
  sendMessageToParent({
    action: "REQUEST_TOKEN",
    reason: "auth_failure",
    error: error,
  })
}

// Listen for messages from the parent window
export function listenToParentMessages(callback: (event: MessageEvent) => void) {
  if (typeof window !== "undefined") {
    window.addEventListener("message", callback)
    return () => window.removeEventListener("message", callback)
  }
  return () => {}
}
